import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { EnhancedDeal, ProjectPLBucket } from '../../types';
import {
  ProjectPLRow as ProjectPLRowData,
} from '../../utils/projectPL';
import { stampHeuristicPoQbAscription } from '../../utils/cardExpensePurchaseApply';
import BucketVarianceTable from './BucketVarianceTable';
import ProjectPLActivityFeed from './ProjectPLActivityFeed';

interface ProjectPLBudgetPanelProps {
  row: ProjectPLRowData;
  formatCurrencyFull: (n: number) => string;
  readOnly?: boolean;
  headerRight?: React.ReactNode;
  onExpenseBucketChange?: (expenseId: string, bucket: ProjectPLBucket | null) => Promise<void>;
  onReassignPayment?: (paymentId: string, dealId: string | null) => Promise<void>;
  onReassignExpense?: (expenseId: string, target: string) => Promise<void>;
  reassignableDeals?: EnhancedDeal[];
}

type PopKind = 'activity' | 'pos' | null;

const ProjectPLBudgetPanel: React.FC<ProjectPLBudgetPanelProps> = ({
  row,
  formatCurrencyFull,
  readOnly,
  headerRight,
  onExpenseBucketChange,
  onReassignPayment,
  onReassignExpense,
  reassignableDeals,
}) => {
  const [pop, setPop] = useState<PopKind>(null);
  const stampedKeys = useRef(new Set<string>());

  const close = useCallback(() => setPop(null), []);
  const openActivity = useCallback(() => setPop('activity'), []);
  const openPos = useCallback(() => setPop('pos'), []);

  useEffect(() => {
    if (!pop) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [pop, close]);

  useEffect(() => {
    if (pop !== 'activity' || readOnly) return;
    const pending = (row.expenses || []).filter(
      expense => expense.poMatchSource === 'heuristic' && expense.purchaseRequisitionId,
    );
    if (!pending.length) return;
    let cancelled = false;
    (async () => {
      for (const expense of pending) {
        const requisitionId = expense.purchaseRequisitionId!;
        const key = `${expense.id}:${requisitionId}`;
        if (stampedKeys.current.has(key)) continue;
        stampedKeys.current.add(key);
        const po = (row.pos || []).find(item => item.id === requisitionId);
        try {
          await stampHeuristicPoQbAscription({
            expenseId: expense.id,
            requisition: {
              id: requisitionId,
              poNumber: expense.poNumber,
              qbBillId: po?.qbBillId,
            },
            entityType: expense.entityType,
            quickbooksId: expense.quickbooksId,
          });
        } catch (err) {
          stampedKeys.current.delete(key);
          console.warn('[projectPL] heuristic PO stamp failed', err);
        }
        if (cancelled) return;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pop, readOnly, row.dealId, row.expenses, row.pos]);

  const pos = row.pos || [];
  const posTotal = useMemo(() => pos.reduce((sum, line) => sum + line.amount, 0), [pos]);

  const modal = pop && createPortal(
    <div className="sa-ppl-modal-backdrop sa-ppl-detail-backdrop" onClick={close}>
      <div
        className="ppl sa-ppl-modal sa-ppl-modal-detail"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={pop === 'pos' ? 'Project purchase orders' : 'Project activity'}
      >
        <div className="sa-ppl-modal-head">
          <div>
            <h3>{pop === 'pos' ? 'Purchase orders' : 'Activity'}</h3>
            <p className="sa-ppl-modal-sub">
              {row.dealTitle}
              {pop === 'pos'
                ? ` · ${pos.length} open`
                : row.cashCollected > 0
                  ? ` · ${formatCurrencyFull(row.cashCollected)} collected`
                  : ''}
            </p>
          </div>
          <div className="sa-ppl-modal-head-actions">
            {pop === 'activity' && headerRight}
            <button type="button" className="sa-ppl-modal-close" onClick={close} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="sa-ppl-modal-body">
          {pop === 'activity' ? (
            <ProjectPLActivityFeed
              row={row}
              formatCurrencyFull={formatCurrencyFull}
              readOnly={readOnly}
              embedded
              hideHeader
              onExpenseBucketChange={onExpenseBucketChange}
              onReassignPayment={onReassignPayment}
              onReassignExpense={onReassignExpense}
              reassignableDeals={reassignableDeals}
            />
          ) : pos.length === 0 ? (
            <div className="sa-ppl-empty">No open POs for this project.</div>
          ) : (
            <div className="sa-ppl-po-list">
              {pos.map(item => (
                <Link
                  key={item.id}
                  className="sa-ppl-po-row"
                  to={`/purchasing?pr=${encodeURIComponent(item.id)}`}
                  onClick={close}
                >
                  <div className="sa-ppl-po-main">
                    <span className="sa-ppl-po-num">{item.label}</span>
                    <span className={`sa-ppl-po-qb sa-ppl-po-qb--${item.qbStatus}`}>{item.qbLabel}</span>
                    <span className="sa-ppl-po-amt">{formatCurrencyFull(item.amount)}</span>
                  </div>
                  {(item.vendorName || item.title || item.bucketLabel) && (
                    <div className="sa-ppl-po-sub">
                      {[item.vendorName, item.title, item.bucketLabel].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </Link>
              ))}
              <div className="sa-ppl-po-total">
                <span>Total</span>
                <span>{formatCurrencyFull(posTotal)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );

  return (
    <div className="sa-ppl-section sa-ppl-section--budget">
      <div className="sa-ppl-section-head">
        <button
          type="button"
          className="sa-ppl-budget-pop-trigger"
          onClick={openActivity}
        >
          Budget vs actual
        </button>
        {Math.abs(row.changeOrderBudget || 0) >= 0.5 && (
          <span
            className="sa-ppl-budget-source-badge"
            title={`Budget = signed proposal ${row.changeOrderBudget < 0 ? '−' : '+'} ${formatCurrencyFull(Math.abs(row.changeOrderBudget))} from approved change orders`}
          >
            Proposal + COs
          </span>
        )}
      </div>
      <BucketVarianceTable
        buckets={row.buckets}
        formatCurrency={formatCurrencyFull}
        onOpenActivity={openActivity}
        onOpenPos={openPos}
      />
      {modal}
    </div>
  );
};

export default ProjectPLBudgetPanel;
